import { PRICING_ENTRY_POINTS, PricingEntryPoint } from './pricingAttribution';

// A purchase started from a recording paywall (Download, Translate, Auto-zoom) brings the user to that recording after
// a successful payment and repeats the action the paywall blocked there; an abandoned purchase goes back to the page the
// paywall was opened on. This remembers where to go across the pricing page, the in-app plan change and the Stripe
// redirect. sessionStorage keeps it to the tab that started the purchase.
const KEY = 'rask_recording_checkout_return';
const TTL_MS = 24 * 60 * 60 * 1000;
// A paid return is acted on by the recording editor right after the redirect; later visits must not repeat the action.
const PAID_TTL_MS = 10 * 60 * 1000;

export const RECORDING_CHECKOUT_ACTIONS = ['autoZoom', 'download', 'localize'] as const;

export type RecordingCheckoutAction = (typeof RECORDING_CHECKOUT_ACTIONS)[number];

export type RecordingCheckoutTarget = {
  action: RecordingCheckoutAction;
  // The recording's page: a successful purchase lands here and the page repeats the action.
  path: string;
  recordingId: string;
  // The page the paywall was opened on (a recordings list, Home), if it isn't the recording's page: a cancelled or
  // abandoned purchase goes back here.
  returnPath?: string;
};

export type RecordingCheckoutReturn = {
  entryPoint: PricingEntryPoint;
  paidAt?: number;
  createdAt: number;
} & RecordingCheckoutTarget;

const isRecordingCheckoutReturn = (value: unknown): value is RecordingCheckoutReturn => {
  if (!value || typeof value !== 'object') return false;

  const { action, entryPoint, paidAt, path, recordingId, returnPath, createdAt } =
    value as Partial<RecordingCheckoutReturn>;

  return (
    RECORDING_CHECKOUT_ACTIONS.some((candidate) => candidate === action) &&
    PRICING_ENTRY_POINTS.some((candidate) => candidate === entryPoint) &&
    typeof createdAt === 'number' &&
    (paidAt === undefined || typeof paidAt === 'number') &&
    typeof path === 'string' &&
    path.startsWith('/') &&
    typeof recordingId === 'string' &&
    recordingId.length > 0 &&
    (returnPath === undefined || (typeof returnPath === 'string' && returnPath.startsWith('/')))
  );
};

const write = (value: RecordingCheckoutReturn) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {}
};

export const clearRecordingCheckoutReturn = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
};

export const readRecordingCheckoutReturn = (): null | RecordingCheckoutReturn => {
  try {
    const raw = sessionStorage.getItem(KEY);

    if (!raw) return null;

    const value: unknown = JSON.parse(raw);

    if (!isRecordingCheckoutReturn(value) || Date.now() - value.createdAt > TTL_MS) {
      clearRecordingCheckoutReturn();
      return null;
    }

    return value;
  } catch {
    return null;
  }
};

/** Where an abandoned purchase goes back to: the page the paywall was opened on. */
export const getRecordingCheckoutBackPath = (target: RecordingCheckoutTarget) => target.returnPath ?? target.path;

/** Called when a recording paywall sends the user to pricing. */
export const rememberRecordingCheckoutReturn = (target: RecordingCheckoutTarget, entryPoint: PricingEntryPoint) =>
  write({ ...target, entryPoint, createdAt: Date.now() });

// Only the checkout that the recording paywall started counts: a later purchase from elsewhere must not be redirected.
const takeMatching = (checkoutEntryPoint: null | PricingEntryPoint) => {
  const stored = readRecordingCheckoutReturn();

  clearRecordingCheckoutReturn();

  return stored && !stored.paidAt && stored.entryPoint === checkoutEntryPoint ? stored : null;
};

/** On a cancelled checkout: where to send the user back, if the checkout came from a recording paywall. */
export const takeCanceledRecordingCheckoutReturn = (checkoutEntryPoint: null | PricingEntryPoint) =>
  takeMatching(checkoutEntryPoint);

/** On a successful checkout: where to send the user, keeping the blocked action for the editor to repeat. */
export const markRecordingCheckoutReturnPaid = (checkoutEntryPoint: null | PricingEntryPoint) => {
  const stored = takeMatching(checkoutEntryPoint);

  if (stored) write({ ...stored, paidAt: Date.now() });

  return stored;
};

/** The return of a purchase that has just succeeded, if any: the recording to go to and the action to repeat there. */
export const readPaidRecordingCheckoutReturn = (): null | RecordingCheckoutReturn => {
  const stored = readRecordingCheckoutReturn();

  if (!stored?.paidAt) return null;

  if (Date.now() - stored.paidAt > PAID_TTL_MS) {
    clearRecordingCheckoutReturn();
    return null;
  }

  return stored;
};

/** The action to repeat in this recording's editor right after a successful checkout, if any. */
export const readPaidRecordingCheckoutAction = (recordingId: string): null | RecordingCheckoutAction => {
  const stored = readPaidRecordingCheckoutReturn();

  return stored?.recordingId === recordingId ? stored.action : null;
};
